import { toast } from 'react-toastify';
import socketService from './socketService';

class VideoUploadManager {
  constructor() {
    this.activeUploads = new Map();
    this.refreshCallbacks = new Map();
  }
  registerRefreshCallback(location, callback) {
    this.refreshCallbacks.set(location, callback);
  }
  unregisterRefreshCallback(location) {
    this.refreshCallbacks.delete(location);
  }
  setupUploadListeners(jobId, currentLocation, options = {}) {
    if (!jobId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('setupUploadListeners called without jobId');
      }
      return;
    }

    const completeHandler = (data) => {
      const eventJobId = data?.jobId?.toString();
      const currentJobId = jobId?.toString();
      if (eventJobId === currentJobId) {
        const upload = this.activeUploads.get(jobId);
        const successMsg = upload?.options?.successMessage || options.successMessage || 'Video uploaded successfully! 🎉';
        this.cleanupListeners(jobId);
        const showSuccessToast = () => {
          try {
            toast.success(successMsg, {
              autoClose: 6000,
              toastId: `upload-success-${jobId}`,
              position: 'top-right',
            });
            return true;
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Error showing success toast:', error);
            }
            return false;
          }
        };
        if (!showSuccessToast()) {
          let retryCount = 0;
          const maxRetries = 15;
          const retryInterval = setInterval(() => {
            retryCount++;
            if (showSuccessToast() || retryCount >= maxRetries) {
              clearInterval(retryInterval);
              if (retryCount >= maxRetries) {
                alert(successMsg);
              }
            }
          }, 300);
        }
        const currentPath = window.location.pathname;
        const isOnForYouVideoPage = currentPath === '/for-you-video' || currentPath.startsWith('/for-you-video');
        if (isOnForYouVideoPage) {
          const refreshCallback = this.refreshCallbacks.get('/for-you-video');
          if (refreshCallback) {
            setTimeout(() => {
              try {
                refreshCallback();
              } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Error calling refresh callback:', error);
                }
              }
            }, 1000);
          }
        }
      }
    };
    const errorHandler = (data) => {
      let errorData = data;
      if (typeof data === 'string') {
        try {
          errorData = JSON.parse(data);
        } catch (e) {
          errorData = { message: data };
        }
      }
      const eventJobId = errorData?.jobId?.toString();
      const currentJobId = jobId?.toString();
      const jobIdMatch = eventJobId === currentJobId ||
        (Array.isArray(data) && data[1]?.jobId?.toString() === currentJobId);
      if (jobIdMatch) {
        this.cleanupListeners(jobId);
        const upload = this.activeUploads.get(jobId);
        const errorMessage = errorData?.message ||
          (Array.isArray(data) && data[1]?.message) ||
          upload?.options?.errorMessage ||
          'An error occurred during upload';

        try {
          const showErrorToast = () => {
            try {
              toast.error(`Upload failed: ${errorMessage}`, {
                autoClose: 8000,
                toastId: `upload-error-${jobId}`,
                position: 'top-right',
              });
              return true;
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Error showing error toast:', error);
              }
              return false;
            }
          };
          if (!showErrorToast()) {
            let retryCount = 0;
            const maxRetries = 15;
            const retryInterval = setInterval(() => {
              retryCount++;
              if (showErrorToast() || retryCount >= maxRetries) {
                clearInterval(retryInterval);
                if (retryCount >= maxRetries) {
                  alert(`Upload failed: ${errorMessage}`);
                }
              }
            }, 300);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error in error handler:', error);
          }
        }
      }
    };
    socketService.on('video-upload-complete', completeHandler);
    socketService.on('video-upload-error', errorHandler);
    this.activeUploads.set(jobId, {
      handlers: {
        complete: completeHandler,
        error: errorHandler,
      },
      location: currentLocation,
      options: options,
    });
  }
  cleanupListeners(jobId) {
    const upload = this.activeUploads.get(jobId);
    if (upload) {
      socketService.off('video-upload-progress', upload.handlers.progress);
      socketService.off('video-upload-complete', upload.handlers.complete);
      socketService.off('video-upload-error', upload.handlers.error);
      this.activeUploads.delete(jobId);
    }
  }
  cleanupAll() {
    this.activeUploads.forEach((upload, jobId) => {
      this.cleanupListeners(jobId);
    });
  }
}
const videoUploadManager = new VideoUploadManager();
export default videoUploadManager;
